import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  Button, 
  OutlineButton, 
  Badge, 
  TableContainer, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableHeader,
  FlexContainer,
  Grid
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import { OfficeFinancialSummary, OfficeIncome, AccountingEntry } from '../../types';
import apiService from '../../services/api';
import { formatCurrency } from '../../utils/formatUtils';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const Summary = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SummaryCard = styled.div<{ type?: string }>`
  background-color: white;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  padding: 1.5rem;
  text-align: center;
  
  h3 {
    margin: 0 0 0.5rem 0;
    color: var(--secondary-color);
    font-size: 1rem;
  }
  
  .amount {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${props => props.type === 'income' ? 'var(--success-color)' : 
      props.type === 'expense' ? 'var(--danger-color)' : 
      props.type === 'budget' ? 'var(--primary-color)' : 'var(--text-color)'};
  }
  
  .label {
    font-size: 0.85rem;
    color: var(--dark-gray);
    margin-top: 0.5rem;
  }
`;

const AmountText = styled.span<{ type: 'positive' | 'negative' | 'neutral' }>`
  font-weight: 500;
  color: ${props => props.type === 'positive' ? 'var(--success-color)' : 
    props.type === 'negative' ? 'var(--danger-color)' : 'var(--text-color)'};
`;

const ProgressBar = styled.div<{ percentage: number }>`
  width: 100%;
  height: 8px;
  background-color: var(--light-gray);
  border-radius: 4px;
  overflow: hidden;
  margin-top: 0.5rem;
  
  .progress {
    height: 100%;
    background-color: ${props => props.percentage > 100 ? 'var(--danger-color)' : 
      props.percentage > 80 ? 'var(--warning-color)' : 'var(--success-color)'};
    width: ${props => Math.min(props.percentage, 100)}%;
    transition: width 0.3s ease;
  }
`;

const OfficeFinancialSummaryComponent: React.FC = () => {
  const { user } = useAuth();
  const [summary, setSummary] = useState<OfficeFinancialSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadFinancialSummary = async () => {
      try {
        setLoading(true);
        
        // Load company balance
        const balanceResponse = await apiService.getCompanyBalance();
        const companyBalance = balanceResponse.data;
        
        // Load office budget requests to calculate financial summary
        const budgetResponse = await apiService.getOfficeBudgetRequests({ limit: 1000 });
        const budgetRequests = budgetResponse.data || [];
        
        // Calculate real financial summary from budget requests
        const totalBudget = budgetRequests.reduce((sum: number, request: any) => sum + (request.amount || 0), 0);
        const approvedRequests = budgetRequests.filter((request: any) => 
          request.status === 'Approved by MD' || request.status === 'Paid'
        );
        const totalIncome = approvedRequests.reduce((sum: number, request: any) => sum + (request.amount || 0), 0);
        const paidRequests = budgetRequests.filter((request: any) => request.status === 'Paid');
        const totalExpenses = paidRequests.reduce((sum: number, request: any) => sum + (request.amount || 0), 0);
        
        // Use real company balance instead of calculated net balance
        const netBalance = companyBalance.currentBalance || 0;
        const budgetUtilization = totalBudget > 0 ? (totalExpenses / totalBudget) * 100 : 0;
        
        // Group by categories
        const categories = budgetRequests.reduce((acc: any[], request: any) => {
          request.categories?.forEach((category: any) => {
            const existing = acc.find(c => c.name === category.name);
            if (existing) {
              existing.budgeted += category.amount || 0;
              if (request.status === 'Paid') {
                existing.spent += category.amount || 0;
              }
            } else {
              acc.push({
                name: category.name,
                budgeted: category.amount || 0,
                spent: request.status === 'Paid' ? (category.amount || 0) : 0,
                remaining: request.status === 'Paid' ? 0 : (category.amount || 0),
                percentage: 0
              });
            }
          });
          return acc;
        }, [] as any[]);
        
        // Calculate percentages
        categories.forEach((category: any) => {
          category.percentage = category.budgeted > 0 ? (category.spent / category.budgeted) * 100 : 0;
          category.remaining = category.budgeted - category.spent;
        });
        
        const realSummary: OfficeFinancialSummary = {
          totalBudget: companyBalance.totalBudget || totalBudget,
          totalIncome,
          totalExpenses: companyBalance.totalSpent || totalExpenses,
          netBalance,
          budgetUtilization,
          currency: 'TZS',
          lastUpdated: new Date(),
          categories,
          companyBalance: {
            currentBalance: companyBalance.currentBalance || 0,
            totalBudget: companyBalance.totalBudget || 0,
            totalSpent: companyBalance.totalSpent || 0,
            remainingBudget: companyBalance.remainingBudget || 0,
            currency: companyBalance.currency || 'TZS',
            lastUpdated: companyBalance.lastUpdated || new Date(),
            lastUpdatedBy: companyBalance.lastUpdatedBy,
            lastTransactionType: companyBalance.lastTransactionType,
            lastTransactionAmount: companyBalance.lastTransactionAmount,
            lastTransactionDescription: companyBalance.lastTransactionDescription
          }
        };
        
        setSummary(realSummary);
      } catch (error) {
        console.error('Error loading financial summary:', error);
        // Fallback to empty summary
        setSummary({
          totalBudget: 0,
          totalIncome: 0,
          totalExpenses: 0,
          netBalance: 0,
          budgetUtilization: 0,
          currency: 'TZS',
          lastUpdated: new Date(),
          categories: [],
          companyBalance: {
            currentBalance: 0,
            totalBudget: 0,
            totalSpent: 0,
            remainingBudget: 0,
            currency: 'TZS',
            lastUpdated: new Date()
          }
        });
      } finally {
        setLoading(false);
      }
    };
    
    if (user) {
      loadFinancialSummary();
    }
  }, [user]);

  // Use the imported formatCurrency function

  if (loading) {
    return <div>Loading financial summary...</div>;
  }

  if (!summary) {
    return (
      <div>
        <PageHeader>
          <h1 className="page-title">Office Financial Summary</h1>
        </PageHeader>
        <Card>
          <CardBody>
            <p>No financial summary data available.</p>
          </CardBody>
        </Card>
      </div>
    );
  }

  const incomeVsExpenses = summary.totalIncome - summary.totalExpenses;
  const budgetVsActual = summary.totalBudget - summary.totalExpenses;

  return (
    <div>
      <PageHeader>
        <div>
          <h1 className="page-title">Office Financial Summary</h1>
          <p style={{ color: 'var(--dark-gray)', margin: '0.5rem 0 0 0' }}>
            Last updated: {summary.lastUpdated.toLocaleDateString()}
          </p>
        </div>
        <FlexContainer gap="0.5rem">
          <OutlineButton as={Link} to="/office">
            Back to Office
          </OutlineButton>
          <Button as={Link} to="/office/finance/new">
            + New Entry
          </Button>
        </FlexContainer>
      </PageHeader>

      <Summary>
        <SummaryCard type="income">
          <h3>Office Income</h3>
          <div className="amount">{formatCurrency(summary.totalIncome, summary.currency)}</div>
          <div className="label">Non-project income</div>
        </SummaryCard>
        
        <SummaryCard type="expense">
          <h3>Actual Expenses</h3>
          <div className="amount">{formatCurrency(summary.totalExpenses, summary.currency)}</div>
          <div className="label">Office expenses</div>
        </SummaryCard>
        
        <SummaryCard type="budget">
          <h3>Office Budget</h3>
          <div className="amount">{formatCurrency(summary.totalBudget, summary.currency)}</div>
          <div className="label">Planned budget</div>
        </SummaryCard>
        
        <SummaryCard>
          <h3>Company Balance</h3>
          <div className="amount" style={{ 
            color: summary.companyBalance?.currentBalance && summary.companyBalance.currentBalance >= 0 ? 'var(--success-color)' : 'var(--danger-color)' 
          }}>
            {formatCurrency(summary.companyBalance?.currentBalance || 0, summary.currency)}
          </div>
          <div className="label">Current available balance</div>
        </SummaryCard>
      </Summary>

      {summary.companyBalance && (
        <Card style={{ marginBottom: '1.5rem' }}>
          <CardHeader>
            <h3>Company Balance Details</h3>
          </CardHeader>
          <CardBody>
            <Grid columns="1fr 1fr 1fr" gap="1rem">
              <div>
                <h4 style={{ marginBottom: '0.5rem', color: 'var(--secondary-color)' }}>Total Budget</h4>
                <div className="amount">{formatCurrency(summary.companyBalance.totalBudget, summary.currency)}</div>
              </div>
              <div>
                <h4 style={{ marginBottom: '0.5rem', color: 'var(--secondary-color)' }}>Total Spent</h4>
                <div className="amount" style={{ color: 'var(--danger-color)' }}>
                  {formatCurrency(summary.companyBalance.totalSpent, summary.currency)}
                </div>
              </div>
              <div>
                <h4 style={{ marginBottom: '0.5rem', color: 'var(--secondary-color)' }}>Remaining Budget</h4>
                <div className="amount" style={{ 
                  color: summary.companyBalance.remainingBudget >= 0 ? 'var(--success-color)' : 'var(--danger-color)' 
                }}>
                  {formatCurrency(summary.companyBalance.remainingBudget, summary.currency)}
                </div>
              </div>
            </Grid>
            {summary.companyBalance.lastTransactionType && (
              <div style={{ 
                marginTop: '1rem', 
                padding: '1rem', 
                background: 'var(--light-gray)', 
                borderRadius: 'var(--border-radius)' 
              }}>
                <h4 style={{ marginBottom: '0.5rem', color: 'var(--secondary-color)' }}>Last Transaction</h4>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div><strong>Type:</strong> {summary.companyBalance.lastTransactionType}</div>
                    <div><strong>Amount:</strong> {formatCurrency(summary.companyBalance.lastTransactionAmount || 0, summary.currency)}</div>
                    <div><strong>Description:</strong> {summary.companyBalance.lastTransactionDescription}</div>
                  </div>
                  <div style={{ fontSize: '0.9rem', color: 'var(--dark-gray)' }}>
                    {summary.companyBalance.lastUpdated.toLocaleDateString()}
                  </div>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader>
          <h3>Financial Analysis</h3>
        </CardHeader>
        <CardBody>
          <Grid columns="1fr 1fr" gap="2rem">
            <div>
              <h4 style={{ marginBottom: '1rem', color: 'var(--secondary-color)' }}>
                Income vs Expenses Analysis
              </h4>
              <div style={{ 
                background: 'var(--light-gray)', 
                padding: '1rem', 
                borderRadius: 'var(--border-radius)',
                marginBottom: '1rem'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span>Office Income:</span>
                  <AmountText type="positive">{formatCurrency(summary.totalIncome, summary.currency)}</AmountText>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span>Office Expenses:</span>
                  <AmountText type="negative">{formatCurrency(summary.totalExpenses, summary.currency)}</AmountText>
                </div>
                <hr style={{ margin: '0.5rem 0', border: 'none', borderTop: '1px solid var(--border-color)' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                  <span>Net Balance:</span>
                  <AmountText type={incomeVsExpenses >= 0 ? 'positive' : 'negative'}>
                    {formatCurrency(incomeVsExpenses, summary.currency)}
                  </AmountText>
                </div>
              </div>
              
              <div style={{ 
                background: 'var(--light-gray)', 
                padding: '1rem', 
                borderRadius: 'var(--border-radius)'
              }}>
                <h5 style={{ margin: '0 0 0.5rem 0', color: 'var(--secondary-color)' }}>
                  Project Commission (5%)
                </h5>
                <p style={{ fontSize: '0.9rem', margin: '0', color: 'var(--dark-gray)' }}>
                  5% of any project income is automatically posted as office income to cover overheads.
                </p>
              </div>
            </div>
            
            <div>
              <h4 style={{ marginBottom: '1rem', color: 'var(--secondary-color)' }}>
                Budget vs Actual Analysis
              </h4>
              <div style={{ 
                background: 'var(--light-gray)', 
                padding: '1rem', 
                borderRadius: 'var(--border-radius)',
                marginBottom: '1rem'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span>Office Budget:</span>
                  <AmountText type="neutral">{formatCurrency(summary.totalBudget, summary.currency)}</AmountText>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span>Actual Expenses:</span>
                  <AmountText type="negative">{formatCurrency(summary.totalExpenses, summary.currency)}</AmountText>
                </div>
                <hr style={{ margin: '0.5rem 0', border: 'none', borderTop: '1px solid var(--border-color)' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                  <span>Variance:</span>
                  <AmountText type={budgetVsActual >= 0 ? 'positive' : 'negative'}>
                    {formatCurrency(budgetVsActual, summary.currency)}
                  </AmountText>
                </div>
              </div>
              
              <div style={{ 
                background: 'var(--light-gray)', 
                padding: '1rem', 
                borderRadius: 'var(--border-radius)'
              }}>
                <h5 style={{ margin: '0 0 0.5rem 0', color: 'var(--secondary-color)' }}>
                  Budget Utilization
                </h5>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span>Utilization:</span>
                  <span style={{ fontWeight: 'bold' }}>{summary.budgetUtilization.toFixed(1)}%</span>
                </div>
                <ProgressBar percentage={summary.budgetUtilization}>
                  <div className="progress"></div>
                </ProgressBar>
              </div>
            </div>
          </Grid>
        </CardBody>
      </Card>

      <Card style={{ marginTop: '1.5rem' }}>
        <CardHeader>
          <h3>Detailed Category Analysis</h3>
        </CardHeader>
        <CardBody>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>Category</TableHeader>
                  <TableHeader>Budgeted</TableHeader>
                  <TableHeader>Actual Spent</TableHeader>
                  <TableHeader>Variance</TableHeader>
                  <TableHeader>Utilization</TableHeader>
                  <TableHeader>Status</TableHeader>
                </TableRow>
              </TableHead>
              <tbody>
                {summary.categories.map((category) => (
                  <TableRow key={category.name}>
                    <TableCell>
                      <strong>{category.name}</strong>
                    </TableCell>
                    <TableCell>
                      <AmountText type="neutral">
                        {formatCurrency(category.budgeted, summary.currency)}
                      </AmountText>
                    </TableCell>
                    <TableCell>
                      <AmountText type="negative">
                        {formatCurrency(category.spent, summary.currency)}
                      </AmountText>
                    </TableCell>
                    <TableCell>
                      <AmountText type={category.remaining >= 0 ? 'positive' : 'negative'}>
                        {formatCurrency(category.remaining, summary.currency)}
                      </AmountText>
                    </TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontSize: '0.9rem', fontWeight: '500' }}>
                          {category.percentage.toFixed(1)}%
                        </span>
                        <ProgressBar percentage={category.percentage}>
                          <div className="progress"></div>
                        </ProgressBar>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={
                          category.percentage > 100 ? 'danger' :
                          category.percentage === 100 ? 'warning' :
                          category.percentage > 80 ? 'info' : 'success'
                        }
                      >
                        {category.percentage > 100 ? 'Over Budget' :
                         category.percentage === 100 ? 'On Budget' :
                         category.percentage > 80 ? 'High Usage' : 'Under Budget'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        </CardBody>
      </Card>

      <div style={{ textAlign: 'right', padding: '1rem 0', color: 'var(--dark-gray)', fontSize: '0.8rem' }}>
        Developed by Sandfish
      </div>
    </div>
  );
};

export default OfficeFinancialSummaryComponent; 